import { useState, useEffect } from 'react';

import { getTargets, createTarget, updateTarget, deleteTarget, getUserDirectory, getCRMStats } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { showSuccessToast, showErrorToast } from '../utils/toastConfig';
import { FiPlus, FiEdit2, FiTrash2, FiRefreshCw } from 'react-icons/fi';
import './crmPages.css';

const formatMoney = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');
const formatDate  = (d) => (d ? new Date(d).toLocaleDateString('en-IN') : '—');

function startOfMonth(d = new Date()) { return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); }
function endOfMonth  (d = new Date()) { return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10); }

const emptyForm = {
  user_id: '',
  period_type: 'Monthly',
  period_start: startOfMonth(),
  period_end: endOfMonth(),
  target_amount: '',
  target_leads: 0,
  target_deals: 0,
  notes: '',
};

export default function SalesTargets() {
  const { user } = useAuth();
  const canEdit = user?.role === 'ceo' || user?.role === 'admin' || user?.role === 'hr';

  const [targets, setTargets] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ leaderboard: [] });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [t, u, s] = await Promise.all([
        getTargets(),
        canEdit ? getUserDirectory() : Promise.resolve({ data: [] }),
        canEdit ? getCRMStats() : Promise.resolve({ data: { leaderboard: [] } }),
      ]);
      setTargets(t.data || []);
      setUsers((u.data || []).filter((x) => x.role === 'sales'));
      setStats(s.data || { leaderboard: [] });
    } catch (err) {
      showErrorToast('Failed to load targets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      user_id: t.user_id,
      period_type: t.period_type,
      period_start: t.period_start ? t.period_start.slice(0, 10) : startOfMonth(),
      period_end:   t.period_end   ? t.period_end.slice(0, 10)   : endOfMonth(),
      target_amount: t.target_amount || '',
      target_leads:  t.target_leads || 0,
      target_deals:  t.target_deals || 0,
      notes: t.notes || '',
    });
    setShowModal(true);
  };

  const handleChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.user_id)        { showErrorToast('Please pick a sales rep'); return; }
    if (!form.period_start || !form.period_end) {
      showErrorToast('Period dates are required'); return;
    }
    setSaving(true);
    try {
      if (editing) {
        await updateTarget(editing.id, form);
        showSuccessToast('Target updated');
      } else {
        await createTarget(form);
        showSuccessToast('Target saved');
      }
      setShowModal(false);
      await loadAll();
    } catch (err) {
      showErrorToast(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete target for ${t.full_name || t.username} (${t.period_type} ${formatDate(t.period_start)})?`)) return;
    try {
      await deleteTarget(t.id);
      showSuccessToast('Target deleted');
      await loadAll();
    } catch (err) {
      showErrorToast(err.response?.data?.message || 'Delete failed');
    }
  };

  function progressPct(achieved, target) {
    if (!target || Number(target) === 0) return 0;
    return Math.min(100, Math.round((Number(achieved) / Number(target)) * 100));
  }

  return (
    <div className="cp-shell">

      <main className="cp-main">
        <div className="cp-page-header">
          <div>
            <h1>{canEdit ? 'Sales Targets & Leaderboard' : 'My Targets'}</h1>
            <p>{canEdit
              ? 'Set revenue targets per salesperson. Achievement updates live from closed deals.'
              : 'Your current targets and how you are tracking against them.'}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="cp-btn cp-btn--ghost" onClick={loadAll}><FiRefreshCw /> Refresh</button>
            {canEdit && (
              <button className="cp-btn cp-btn--primary" onClick={openCreate}><FiPlus /> New Target</button>
            )}
          </div>
        </div>

        {canEdit && stats.leaderboard && stats.leaderboard.length > 0 && (
          <div className="cp-card" style={{ marginBottom: 20 }}>
            <h3 style={{ marginTop: 0 }}>Sales Leaderboard</h3>
            {stats.leaderboard.map((row, idx) => (
              <div key={row.id} className="cp-leader-row">
                <div className="cp-leader__rank">{idx + 1}</div>
                <div>
                  <div style={{ fontWeight: 600 }}>{row.full_name || row.username}</div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                    {row.deals_won || 0} won · {row.deals_lost || 0} lost · {row.leads_owned || 0} leads
                  </div>
                </div>
                <div style={{ fontWeight: 700, color: '#111827' }}>{formatMoney(row.revenue_won)}</div>
              </div>
            ))}
          </div>
        )}

        {loading ? (
          <div className="cp-empty">Loading targets...</div>
        ) : targets.length === 0 ? (
          <div className="cp-card cp-empty">
            <div className="cp-empty__title">No targets set yet</div>
            {canEdit && <div>Click "New Target" to set the first one.</div>}
          </div>
        ) : (
          <div className="cp-table-wrap">
            <table className="cp-table">
              <thead>
                <tr>
                  <th>Salesperson</th>
                  <th>Period</th>
                  <th>Target</th>
                  <th>Achieved</th>
                  <th style={{ minWidth: 200 }}>Progress</th>
                  <th>Deals</th>
                  {canEdit && <th style={{ width: 110 }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {targets.map((t) => {
                  const pct = progressPct(t.achieved_amount, t.target_amount);
                  return (
                    <tr key={t.id}>
                      <td style={{ fontWeight: 600 }}>{t.full_name || t.username}</td>
                      <td>
                        <div>{t.period_type}</div>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>{formatDate(t.period_start)} → {formatDate(t.period_end)}</div>
                      </td>
                      <td>{formatMoney(t.target_amount)}</td>
                      <td style={{ fontWeight: 600, color: pct >= 100 ? '#15803d' : '#111827' }}>
                        {formatMoney(t.achieved_amount)}
                      </td>
                      <td>
                        <div style={{ fontSize: 12, marginBottom: 2 }}>{pct}%</div>
                        <div className="cp-progress"><div className="cp-progress__bar" style={{ width: pct + '%' }} /></div>
                      </td>
                      <td>
                        {t.achieved_deals || 0} won
                        {t.target_deals ? ` / ${t.target_deals}` : ''}
                      </td>
                      {canEdit && (
                        <td>
                          <button className="cp-btn cp-btn--ghost cp-btn--sm" onClick={() => openEdit(t)}><FiEdit2 /></button>
                          <button className="cp-btn cp-btn--danger cp-btn--sm" onClick={() => handleDelete(t)} style={{ marginLeft: 4 }}><FiTrash2 /></button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {showModal && canEdit && (
          <div className="cp-modal-backdrop" onClick={() => !saving && setShowModal(false)}>
            <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
              <div className="cp-modal__header">
                <h2>{editing ? 'Edit Target' : 'New Target'}</h2>
                <button className="cp-modal__close" onClick={() => setShowModal(false)}>×</button>
              </div>
              <form className="cp-form" onSubmit={handleSubmit}>
                <div className="cp-field cp-field--full">
                  <label>Salesperson *</label>
                  <select value={form.user_id} onChange={(e) => handleChange('user_id', e.target.value)} required disabled={!!editing}>
                    <option value="">— Select —</option>
                    {users.map((u) => <option key={u.id} value={u.id}>{u.full_name || u.username}</option>)}
                  </select>
                </div>
                <div className="cp-field">
                  <label>Period Type</label>
                  <select value={form.period_type} onChange={(e) => handleChange('period_type', e.target.value)} disabled={!!editing}>
                    <option value="Monthly">Monthly</option>
                    <option value="Quarterly">Quarterly</option>
                    <option value="Yearly">Yearly</option>
                  </select>
                </div>
                <div className="cp-field"></div>
                <div className="cp-field">
                  <label>Period Start *</label>
                  <input type="date" value={form.period_start} onChange={(e) => handleChange('period_start', e.target.value)} disabled={!!editing} required />
                </div>
                <div className="cp-field">
                  <label>Period End *</label>
                  <input type="date" value={form.period_end} onChange={(e) => handleChange('period_end', e.target.value)} required />
                </div>
                <div className="cp-field">
                  <label>Revenue Target (₹)</label>
                  <input type="number" step="0.01" value={form.target_amount} onChange={(e) => handleChange('target_amount', e.target.value)} />
                </div>
                <div className="cp-field">
                  <label>Leads Target</label>
                  <input type="number" value={form.target_leads} onChange={(e) => handleChange('target_leads', e.target.value)} />
                </div>
                <div className="cp-field cp-field--full">
                  <label>Deals Target</label>
                  <input type="number" value={form.target_deals} onChange={(e) => handleChange('target_deals', e.target.value)} />
                </div>
                <div className="cp-field cp-field--full">
                  <label>Notes</label>
                  <textarea rows={2} value={form.notes} onChange={(e) => handleChange('notes', e.target.value)} />
                </div>
                <div className="cp-form-actions">
                  <button type="button" className="cp-btn cp-btn--ghost" onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="submit" className="cp-btn cp-btn--primary" disabled={saving}>
                    {saving ? 'Saving…' : (editing ? 'Save Changes' : 'Save Target')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
